import type { KeyboardEvent } from "react";

// MoodQuadrantGrid — the Mood-Meter check-in picker (Homebase pre-session flow).
// A 2×2 energy × pleasantness grid of circles (no axis labels: each circle names
// both of its axes); row-major red / yellow /
// blue / green, each mapped to a DS status color (red→danger, yellow→warning,
// blue→info, green→success) via token classes — never hardcoded hex. Controlled
// single-select; the exported MOOD_QUADRANTS data (presession.jsx verbatim) is
// the single source the flow renders word chips from.

export type MoodQuadrant = "red" | "yellow" | "blue" | "green";

export interface MoodQuadrantData {
    key: MoodQuadrant;
    energy: string;
    pleasantness: string;
    words: string[];
}

// presession.jsx HB_QUADS, verbatim (design source of truth).
// eslint-disable-next-line react-refresh/only-export-components -- contracts §5 mandates co-locating the quadrant/word data with the grid (single source for the flow's word chips); dova-ds is consumed as a source library, so fast-refresh boundaries don't apply.
export const MOOD_QUADRANTS: MoodQuadrantData[] = [
    {
        key: "red",
        energy: "High energy",
        pleasantness: "Unpleasant",
        words: ["Anxious", "Frustrated", "Angry", "Overwhelmed", "Tense", "Defensive", "Hurt", "Resentful"],
    },
    {
        key: "yellow",
        energy: "High energy",
        pleasantness: "Pleasant",
        words: ["Hopeful", "Excited", "Motivated", "Eager", "Energized", "Optimistic", "Playful", "Open"],
    },
    {
        key: "blue",
        energy: "Low energy",
        pleasantness: "Unpleasant",
        words: ["Sad", "Tired", "Drained", "Lonely", "Discouraged", "Numb", "Disconnected", "Down"],
    },
    {
        key: "green",
        energy: "Low energy",
        pleasantness: "Pleasant",
        words: ["Calm", "Content", "Relaxed", "Grounded", "Safe", "Peaceful", "Reassured", "Settled"],
    },
];

export interface MoodQuadrantGridProps {
    value: MoodQuadrant | null;
    onChange: (quadrant: MoodQuadrant) => void;
}

const QUAD_STATUS: Record<MoodQuadrant, string> = {
    red: "danger",
    yellow: "warning",
    blue: "info",
    green: "success",
};

// Selected colours ride the dedicated --mood-* tokens (tokens.css), not the status
// triads: one lightness and chroma across all four, so no circle outweighs another.
// The fill lives on its own layer (below) so it can bloom; the border stays on the button.
const SELECTED_BORDER: Record<MoodQuadrant, string> = {
    red: "border-mood-red-bd",
    yellow: "border-mood-yellow-bd",
    blue: "border-mood-blue-bd",
    green: "border-mood-green-bd",
};

// Hover previews the circle's own border colour, so the ring you're about to pick
// already speaks its mood (was the generic --interactive sage).
const HOVER_BORDER: Record<MoodQuadrant, string> = {
    red: "hover:border-mood-red-bd",
    yellow: "hover:border-mood-yellow-bd",
    blue: "hover:border-mood-blue-bd",
    green: "hover:border-mood-green-bd",
};

const FILL: Record<MoodQuadrant, string> = {
    red: "bg-mood-red-bg",
    yellow: "bg-mood-yellow-bg",
    blue: "bg-mood-blue-bg",
    green: "bg-mood-green-bg",
};

// Centre bloom. Selecting grows the tint layer from 0.4 to 1 while it fades in, so the
// circle reads as FILLING rather than popping (never from scale 0). Only scale and
// opacity move, so it stays on the compositor. Asymmetric on purpose: in 300ms, out
// 150ms, so the new pick leads and the old one steps back. The border and label colour
// settle over 200ms on the same curve; the press dips to 0.97 for instant feedback.
// Reduced motion keeps the colour crossfade and drops every scale.
// Written out in full (no interpolation) so Tailwind's scanner sees every class. The
// transitions name `scale`, not `transform`: Tailwind v4's scale-* utilities set the CSS
// `scale` property, so a transform transition would never fire.
const BUTTON_MOTION =
    "[transition:scale_160ms_var(--ease-out),border-color_200ms_var(--ease-out)] active:scale-[0.97] motion-reduce:active:scale-100";
const FILL_MOTION_IN =
    "scale-100 opacity-100 [transition:scale_300ms_var(--ease-out),opacity_300ms_var(--ease-out)]";
const FILL_MOTION_OUT =
    "scale-[0.4] opacity-0 motion-reduce:scale-100 [transition:scale_150ms_var(--ease-out),opacity_150ms_var(--ease-out)]";
const LABEL_MOTION = "[transition:color_200ms_var(--ease-out)]";

export function MoodQuadrantGrid({ value, onChange }: MoodQuadrantGridProps) {
    function commitOnKey(event: KeyboardEvent<HTMLButtonElement>, quadrant: MoodQuadrant) {
        if (event.key === "Enter" || event.key === " ") {
            // preventDefault stops native button activation from double-firing.
            event.preventDefault();
            onChange(quadrant);
        }
    }

    return (
        // Fixed-size columns, centered as a block, with ONE gap for both axes: so the
        // space between circles is the same horizontally and vertically, however
        // wide the container is. Columns shrink (minmax 0) on a narrow phone.
        <div className="grid grid-cols-[repeat(2,minmax(0,12.5rem))] justify-center gap-5">
            {MOOD_QUADRANTS.map((quad) => {
                const selected = value === quad.key;
                return (
                    <button
                        key={quad.key}
                        type="button"
                        aria-label={`${quad.energy} · ${quad.pleasantness}`}
                        aria-pressed={selected}
                        data-status={QUAD_STATUS[quad.key]}
                        onClick={() => onChange(quad.key)}
                        onKeyDown={(e) => commitOnKey(e, quad.key)}
                        className={[
                            "relative flex aspect-square w-full cursor-pointer flex-col items-center justify-center rounded-full",
                            "border-2 bg-surface px-3 text-center",
                            BUTTON_MOTION,
                            selected ? SELECTED_BORDER[quad.key] : `border-border ${HOVER_BORDER[quad.key]}`,
                        ].join(" ")}
                    >
                        <span
                            aria-hidden="true"
                            className={`pointer-events-none absolute inset-0 rounded-full ${FILL[quad.key]} ${
                                selected ? FILL_MOTION_IN : FILL_MOTION_OUT
                            }`}
                        />
                        {/* Title Case by CSS, not by editing the data: `energy`/`pleasantness`
                            are the locked definition of a stored quadrant (me/labelLock.ts), and
                            the accessible name keeps them verbatim. `relative` lifts the words
                            above the fill layer. */}
                        <span className={`relative block text-lg font-semibold leading-snug capitalize ${LABEL_MOTION} ${selected ? "text-primary" : "text-tertiary"}`}>{quad.energy}</span>
                        <span className={`relative mt-1 block text-lg font-semibold leading-snug capitalize ${LABEL_MOTION} ${selected ? "text-primary" : "text-tertiary"}`}>{quad.pleasantness}</span>
                    </button>
                );
            })}
        </div>
    );
}
